import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import { isNative, nativePlatform } from "./lib/platform";
import { supabase } from "./integrations/supabase/client";

// Tag <body> so CSS can style native shells (Capacitor) without affecting the web build.
if (typeof document !== "undefined" && isNative()) {
  document.body.classList.add("platform-native");
  const p = nativePlatform();
  if (p === "ios") document.body.classList.add("platform-ios");
  if (p === "android") document.body.classList.add("platform-android");

  // Native-only setup: status bar, keyboard, splash. Lazy-load so web bundle is unaffected.
  (async () => {
    try {
      const [{ StatusBar, Style }, { Keyboard, KeyboardResize }, { SplashScreen }] = await Promise.all([
        import("@capacitor/status-bar"),
        import("@capacitor/keyboard"),
        import("@capacitor/splash-screen"),
      ]);
      const isDark = document.documentElement.classList.contains("dark");
      await StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {});
      await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light }).catch(() => {});
      await Keyboard.setResizeMode({ mode: KeyboardResize.Native }).catch(() => {});
      await SplashScreen.hide({ fadeOutDuration: 200 }).catch(() => {});
    } catch (_) { /* plugin not available */ }
  })();

  // OAuth deep-link bridge — when the user finishes sign-in in the
  // system browser, the bridge page forwards them to
  //   uk.neuramed.claira://oauth#access_token=...&refresh_token=...
  // which iOS hands to the app via the appUrlOpen event.
  (async () => {
    try {
      const [{ App: CapApp }, { Browser }] = await Promise.all([
        import("@capacitor/app"),
        import("@capacitor/browser"),
      ]);
      CapApp.addListener("appUrlOpen", async ({ url }) => {
        if (!url || !url.startsWith("uk.neuramed.claira://oauth")) return;
        try {
          // Accept tokens from either query string or URL fragment so the
          // app keeps working with older bridge pages still in the wild.
          const queryPart = url.split("?")[1]?.split("#")[0] ?? "";
          const hashPart = url.split("#")[1] ?? "";
          const params = new URLSearchParams(queryPart || hashPart);
          const access_token = params.get("access_token");
          const refresh_token = params.get("refresh_token");
          if (access_token && refresh_token) {
            await supabase.auth.setSession({ access_token, refresh_token });
          }
        } finally {
          await Browser.close().catch(() => {});
          // Send the user to onboarding inside the app.
          window.location.replace("/complete-profile");
        }
      });
    } catch (_) { /* plugins unavailable */ }
  })();
}

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <App />
  </HelmetProvider>
);
